/*
 * Monsuri PDP — sticky add-to-cart bar.
 *
 * Shows when the in-page Add-to-cart scrolls out of view. Its Add button triggers
 * the in-page Add-to-cart (inAtc.click()) rather than re-implementing the cart, so it
 * inherits the theme's ajax add + cart drawer AND the "Complete the set" selection
 * (the bundle JS sets the form variant) AND the gift note. Label + price + the
 * disabled/sold-out state mirror the in-page ATC. Vanilla, idempotent.
 *
 * Visibility = rAF-throttled scroll/resize, showing the bar once the in-page ATC has
 * fully scrolled above the viewport (its bottom edge passes the top). Self-cleans if
 * the product section is re-rendered (theme editor).
 */
(function () {
  'use strict';

  function init(root) {
    if (root.hasAttribute('data-pdp-sticky-ready')) return; // idempotent
    root.setAttribute('data-pdp-sticky-ready', '');

    var scope = root.closest('.product-main') || document;
    var form = scope.querySelector('[data-product-form] form');
    var inAtc = form && form.querySelector('.add_to_cart');
    if (!form || !inAtc) return; // no buy box → leave the bar hidden
    var inAtcText = inAtc.querySelector('.text');

    var label = root.querySelector('[data-pdp-sticky-label]');
    var nameEl = root.querySelector('[data-pdp-sticky-name]');
    var metaPriceEl = root.querySelector('[data-pdp-sticky-meta-price]');
    var metaPriceFallback = metaPriceEl ? metaPriceEl.textContent : ''; // server default, kept if no live price resolves
    var addBtn = root.querySelector('[data-pdp-sticky-add]');
    var setModule = scope.querySelector('[data-pdp-set]');
    // Same canonical-variant-field rule as pdp-complete-the-set.js: accept a hidden
    // <input name="id"> form too (future-proofing), excluding the set module's own kit hidden.
    var select = form.querySelector('select[name="id"], input[name="id"]:not([data-pdp-set-id])');

    var wrap = form.closest('[data-product-form]');
    var product = null;
    if (wrap && wrap.getAttribute('data-product')) {
      try { product = JSON.parse(wrap.getAttribute('data-product')); } catch (e) { /* no live price */ }
    }
    var moneyFormat = (wrap && wrap.getAttribute('data-money-format')) || '${{amount}}';
    function money(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try { return window.Shopify.formatMoney(cents, moneyFormat); } catch (e) {}
      }
      return '$' + (cents / 100).toFixed(2);
    }

    function atcDisabled() { return inAtc.disabled || inAtc.classList.contains('disabled'); }

    // Mirror the in-page ATC (label/disabled) + the active single/set selection: the NAME
    // becomes the selected option's product name (single = this product, set = the kit) and
    // the price updates beside it. No own control — the buy-box radios drive the choice
    // (set JS stamps data-active + the in-page ATC); we only reflect it.
    function sync() {
      if (label && inAtcText) label.textContent = inAtcText.textContent.trim();
      if (addBtn) {
        var dis = atcDisabled();
        addBtn.disabled = dis;                                  // mirror busy/sold-out
        addBtn.setAttribute('aria-disabled', dis ? 'true' : 'false');
      }
      if (!metaPriceEl) return;
      var isSet = !!(setModule && setModule.getAttribute('data-active') === 'set');
      var priceStr = '';
      if (isSet) {
        priceStr = setModule.getAttribute('data-set-price') || '';
      } else if (product && product.variants && select) {       // single → live variant price
        var vid = parseInt(select.value, 10);
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].id === vid) { priceStr = money(product.variants[i].price); break; }
        }
      }
      if (!priceStr && setModule) priceStr = setModule.getAttribute('data-single-price') || '';
      // Name = the selected option's product name (single = this product, set = the kit).
      if (nameEl && setModule) {
        var nm = (isSet ? setModule.getAttribute('data-set-name') : setModule.getAttribute('data-single-name')) || '';
        if (nm) nameEl.textContent = nm;
      }
      // Price sits next to the name (name ellipsizes, price never truncates); keep the
      // server default if no live/set price resolves (e.g. missing product JSON).
      metaPriceEl.textContent = priceStr || metaPriceFallback;
    }

    if (addBtn) addBtn.addEventListener('click', function () { if (atcDisabled()) return; inAtc.click(); });

    if (select) select.addEventListener('change', function () { window.setTimeout(sync, 0); });
    if (setModule) {
      var radios = setModule.querySelectorAll('input[type="radio"]');
      for (var r = 0; r < radios.length; r++) radios[r].addEventListener('change', function () { window.setTimeout(sync, 0); });
    }

    // One observer on the in-page ATC: label text (subtree) + disabled/class attrs.
    var mo = null;
    if ('MutationObserver' in window) {
      mo = new MutationObserver(sync);
      mo.observe(inAtc, { attributes: true, attributeFilter: ['disabled', 'class'], childList: true, characterData: true, subtree: true });
    }

    // Visibility: show the bar once the in-page ATC has fully scrolled ABOVE the
    // viewport (bottom edge past the top). Live position each scroll frame, rAF-
    // throttled. Self-cleans if the section is re-rendered (the ATC node detaches).
    var ticking = false;
    var rafId = null;
    function cleanup() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('shopify:section:unload', onUnload);
      if (rafId) window.cancelAnimationFrame(rafId);
      if (mo) mo.disconnect();
    }
    function onUnload(e) { if (e.target && e.target.contains(root)) cleanup(); }
    function update() {
      ticking = false;
      if (!inAtc.isConnected) { cleanup(); return; } // section replaced via scroll path
      root.classList.toggle('is-visible', inAtc.getBoundingClientRect().bottom < 0);
    }
    function onScroll() { if (!ticking) { ticking = true; rafId = window.requestAnimationFrame(update); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('shopify:section:unload', onUnload); // clean up even with no scroll

    sync();
    update();
  }

  function initAll() {
    var r = document.querySelector('[data-pdp-sticky]');
    if (r) init(r);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll); // theme-editor re-render
})();
